import { chunk } from 'lodash';
import type { SuccessResponseJSON } from 'openapi-typescript-helpers';
import useSWR from 'swr';
import type { IdentityUser } from '@/common/types/user';
import { identityApi } from '$api-clients';
import type { IdentitySchema } from '$api-clients/generated';
import { IDS_CHUNK_SIZE } from '../types';

export type IdentityTeam = SuccessResponseJSON<
  IdentitySchema.paths['/teams']['get']
>['data'][number];

function dedupeAndSort(ids: string[]): string[] {
  return [...new Set(ids)].sort();
}

async function fetchUsersByIds(ids: string[]): Promise<IdentityUser[]> {
  const responses = await Promise.all(
    chunk(ids, IDS_CHUNK_SIZE).map((idsChunk) =>
      identityApi.GET('/users', {
        params: { query: { ids: idsChunk.join(',') } },
      }),
    ),
  );
  // openapi-fetch doesn't throw on HTTP errors. Fail the whole fetch if any
  // chunk errored so SWR surfaces an error state instead of silently rendering
  // a partial list (the very confusion this fix set out to remove).
  if (responses.some((response) => response.error)) {
    throw new Error('Failed to fetch one or more user chunks');
  }
  return responses.flatMap((response) => response.data?.data ?? []);
}

async function fetchTeamsByIds(ids: string[]): Promise<IdentityTeam[]> {
  const responses = await Promise.all(
    chunk(ids, IDS_CHUNK_SIZE).map((idsChunk) =>
      identityApi.GET('/teams', {
        params: { query: { ids: idsChunk.join(',') } },
      }),
    ),
  );
  if (responses.some((response) => response.error)) {
    throw new Error('Failed to fetch one or more team chunks');
  }
  return responses.flatMap((response) => response.data?.data ?? []);
}

/**
 * Resolves users by ID, chunking the request so a large filter list (e.g. a
 * big team's agents) doesn't overflow the request headers and 431. Returns
 * `undefined` while loading, matching the underlying SWR semantics.
 */
export function useChunkedUsers(ids: string[]): {
  users: IdentityUser[] | undefined;
  isLoading: boolean;
} {
  const sortedIds = dedupeAndSort(ids);
  const { data, isLoading } = useSWR(
    sortedIds.length > 0
      ? ['reports-chunked-users', sortedIds.join(',')]
      : null,
    () => fetchUsersByIds(sortedIds),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  return { users: data, isLoading };
}

/**
 * Resolves teams by ID, chunking the request to avoid 431 errors on large
 * filter lists. See {@link useChunkedUsers}.
 */
export function useChunkedTeams(ids: string[]): {
  teams: IdentityTeam[] | undefined;
  isLoading: boolean;
} {
  const sortedIds = dedupeAndSort(ids);
  const { data, isLoading } = useSWR(
    sortedIds.length > 0
      ? ['reports-chunked-teams', sortedIds.join(',')]
      : null,
    () => fetchTeamsByIds(sortedIds),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  return { teams: data, isLoading };
}
